import Box                      from '@/components/box/Box';
import { Button }               from '@chakra-ui/react';
import FlagIcon                 from '@/pages/Settings/components/FlagIcon';
import LanguageModel            from '@/models/services/fondations/LanguageModel';
import LanguageRow              from '@/pages/Settings/components/LanguageRow';
import React                    from 'react';
import SortableList             from '@/components/sortableList/SortableList';
import { SortableListItem }     from '@/components/sortableList/SortableList';
import Spinner                  from '@/components/spinner/Spinner';
import { Text }                 from '@chakra-ui/react';
import UserLanguageModel        from '@/models/services/fondations/UserLanguageModel';
import _sortBy                  from 'lodash/sortBy';
import authStore                from '@/stores/authStore';
import { getIdFromUrn }         from '@widesk-core/tools/identifier';
import { toaster }              from '@/components/ui/toaster';
import useApiCollection         from '@widesk-core/hooks/useApiCollection';
import useFetch                 from '@widesk-core/hooks/useFetch';
import { useTranslation }       from 'react-i18next';

type LanguageBoxItem = {
	id: number;
	urn: string;
    position: number;
    render: () => JSX.Element;
}

type LanguageBoxItems = LanguageBoxItem[]

const LanguageBox = () => {
	const { t } = useTranslation();

	const languageCollection = useApiCollection(LanguageModel);
	const userLanguageCollection = useApiCollection(UserLanguageModel);

	const [languageBoxItems, setLanguageBoxItems] = React.useState<LanguageBoxItems>([]);
	const [isSubmitting, setIsSubmitting] = React.useState(false);

	const getLanguageBoxItems: () => LanguageBoxItems = () => {
		
		return languageCollection.map(language => {

			const userLanguage = userLanguageCollection.find(userLanguage => userLanguage.getUrn('language') === language.urn);
			
			return { 
				id: parseInt(getIdFromUrn(language.urn)),
				urn: language.urn,
				position: userLanguage ? userLanguage.position : (languageCollection.length - 1),
				render: () => <LanguageRow flagIcon={<FlagIcon languageKey={language.locale as LanguageKey} />} languageLabel={t(`app.languages.${language.locale}`)} />,
			};
		});
	};

	const { isLoading } = useFetch(async () => {
		await Promise.all([
			await languageCollection.list(),
			await userLanguageCollection.listBy([authStore.currentUser.id], 'user'),
		]);

		const languageBoxItems = getLanguageBoxItems();		

		const languageBoxItemsByPosition = _sortBy(languageBoxItems, m => m.position);

		setLanguageBoxItems(languageBoxItemsByPosition);

	}, true);

	const handleListChange = (items: SortableListItem[]) => {
		const newlanguageBoxItems = items.map((item, index) => {

			const language = languageCollection.find(language => parseInt(language.id) === item.id);

			return {
				id: item.id,
				urn: language?.urn,
				position: index,
				render: () => item.render(),
			}
		})
		setLanguageBoxItems(newlanguageBoxItems);
	};

	const submitUserLanguages = async () => {

		try {
			setIsSubmitting(true);

			const submitUserLanguage = (item: LanguageBoxItem) => {
				return new Promise((resolve) => {
					const userLanguage = userLanguageCollection.find(userLanguage => userLanguage.getId('language') === item.id);
	
					if (userLanguage) {
						userLanguage.patch({
							position: item.position 
						}).then(response => {
							return resolve(response);
						});
					} else {
						const newUserLanguage = new UserLanguageModel();
						newUserLanguage.patch({
							user: authStore.currentUser.urn,
							language: item.urn,
							position: item.position,
						}).then(() => {
							userLanguageCollection.listBy([authStore.currentUser.id], 'user')
								.then(response => {
									return resolve(response);
								}
							)
						});
					}
				})
			}

			for (const item of languageBoxItems) {
				await submitUserLanguage(item);
			}

			toaster.create({
				title: t('pages.settings.languageBox.save.success'),
				type: 'success'
			});

		} catch (error) {
			toaster.create({
				title: t('pages.settings.languageBox.save.error'),
				type: 'error'
			});
			console.log(error);
		} finally {
			setIsSubmitting(false);
		}

	}

	if (isLoading) {
		return <Spinner />
	};
	
	if (languageBoxItems) {
		return (
			<Box>
				{
					isSubmitting ? 
					<Spinner />
					:
					<>
						<Text className='poppins-semibold' fontSize="18px">{t('pages.settings.languageBox.title')}</Text>
						<Text
							fontSize="14px"
						>
							{t('pages.settings.languageBox.description')}
						</Text>
						<SortableList items={languageBoxItems} onListChange={handleListChange}/>
						<Button
							marginTop="10px"
							textWrap="wrap"
							onClick={submitUserLanguages}
						>
							{t('pages.settings.languageBox.save.label')}
						</Button>
					</>
				}
			</Box>
		);
	};

	return <></>;
}

export default LanguageBox;